import { useFormikContext } from 'formik'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import type { OrchestratedOfferingListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import { centsToDollars } from '@b-stock/fe-core/bstock-react'

import { calculateQuickBidSuggestions } from '@/buyer-portal/components/features/listing/ListingActionColumn/Bid/BidActions/QuickBidButtons'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import newSellerStorefrontsQuery from '@/queries/newSellerStorefrontsQuery'

import { type MakeAnOfferFormValues } from '../../offering/SimpleOfferActionModule/MakeAnOfferFormProvider'
import { useOfferAnalytics } from '../../offering/useOfferAnalytics'
import { QuickActionButtonRow, QuickActionButton } from '../ui'
import { useOfferAmount } from './useOfferAmount'

// Quick Offer, like Quick Bid
export const SimpleOfferQuickButtons = ({
  listing,
  disabled = false,
}: {
  listing: OrchestratedOfferingListingDetails
  disabled?: boolean
}) => {
  const { contract, offering } = listing
  const bidBasis = contract?.bidBasis ?? 'ABSOLUTE'
  const bidPrecision = offering?.bidPrecision ?? 1

  const { setFieldValue, submitForm, values } =
    useFormikContext<MakeAnOfferFormValues>()
  const { trackButtonClicked } = useAnalytics()
  const isNewSellerTreatment =
    useFlag(FeatureFlags.NEW_SELLER_ALP_LABEL) === 'treatment'
  const { data: newSellerIds } = newSellerStorefrontsQuery.useWithOpts([], {
    enabled: isNewSellerTreatment,
  })

  const getOfferAnalytics = useOfferAnalytics(listing)
  const { isValidOffer, renderOfferAmount } = useOfferAmount(listing)

  const base = values.currentOfferAmount || values.minOfferAmount
  const suggestions = calculateQuickBidSuggestions(
    base,
    bidPrecision,
    bidBasis
  ).filter(isValidOffer)

  const handleClick = async (amount: number, buttonNumber: 1 | 2 | 3) => {
    trackButtonClicked({
      buttonName: 'quick_offer',
      button_number: buttonNumber,
      offer_amt_usd: centsToDollars(amount),
      quick_offer_options: suggestions.map(centsToDollars),
      is_new_seller:
        isNewSellerTreatment &&
        (newSellerIds?.has(listing.storefrontId) ?? false),
      ...getOfferAnalytics(),
    })

    await setFieldValue('amount', amount)
    await submitForm()
  }

  return (
    <QuickActionButtonRow>
      {suggestions.map((amount, index) => (
        <QuickActionButton
          key={amount}
          type="button"
          disabled={disabled}
          onClick={() => handleClick(amount, (index + 1) as 1 | 2 | 3)}
        >
          {renderOfferAmount(amount)}
        </QuickActionButton>
      ))}
    </QuickActionButtonRow>
  )
}
